import { Col, Container, Row } from "reactstrap";
import Select from "react-select";
import MainLayout from "../../layouts/MainLayout";
import {
  FaInstagram, FaPhoneAlt, FaTiktok, FaWhatsapp
} from "react-icons/fa";
import { IoShareOutline, IoSearchOutline } from "react-icons/io5";
import PropertyCard from "../../components/Cards/PropertyCard";
import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_URL, STORAGE_URL } from "../../utils/constants";
import { ReactComponent as EmptyIcon } from '../../assets/images/icons/empty.svg';
import DefaultButton from "../../components/Buttons/Default";
import {
  getOfficePhoneNumber,
  getOfficePhoneNumberWithoutCountryCode,
  getOfficeWhatsAppNumber,
  handleInstagramLink,
  handleTikTokLink,
  tField
} from "../../utils/helpers";
import { SelectCustomStyles } from "../../utils/styles";
import { useTranslation } from "react-i18next";
import './styles.css';
import { RiVerifiedBadgeFill } from "react-icons/ri";

function OfficePage() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [officeLoading, setOfficeLoading] = useState(true);
  const [office, setOffice] = useState(null);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [properties, setProperties] = useState([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    property_for: '',
    category_id: '',
    city_ids: ''
  });

  const getOffice = () => {
    const cacheKey = `office_data_${params.id}`;
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      const p = JSON.parse(cached);
      if (!p.office?.is_office) navigate('/404');
      setOffice(p.office);
      setProperties(p.properties);
      setTotalProperties(p.totalProperties);
      setHasMorePages(p.hasMorePages);
      setCurrentPage(p.currentPage);
      setStates(p.states);
      setOfficeLoading(false);
      setPropertiesLoading(false);
      getCategories();
      return;
    }

    setOfficeLoading(true);
    setPropertiesLoading(true);

    fetch(API_URL + '/offices/' + params.id, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
    })
    .then(r => r.json())
    .then(data => {
      if (data.success) {
        if (!data.office?.is_office) navigate('/404');
        const props = data.properties?.data || [];
        const total = data.properties?.total || 0;
        const more  = data.properties?.current_page < data.properties?.last_page;
        const page  = data.properties?.current_page || 1;

        setOffice(data.office);
        setProperties(props);
        setTotalProperties(total);
        setHasMorePages(more);
        setCurrentPage(page);
        setStates(data.states || []);

        sessionStorage.setItem(cacheKey, JSON.stringify({
          office: data.office, properties: props,
          totalProperties: total, hasMorePages: more,
          currentPage: page, states: data.states || []
        }));
        getCategories();
      }
      setOfficeLoading(false);
      setPropertiesLoading(false);
    })
    .catch(() => { setOfficeLoading(false); setPropertiesLoading(false); });
  };

  useEffect(() => { getOffice(); }, [params.id]);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMorePages) return;
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 200) loadMore();
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMorePages, currentPage]);

  const getCategories = () => {
    const key = 'categories_data';
    const cached = sessionStorage.getItem(key);
    if (cached) { setCategories(JSON.parse(cached)); return; }
    fetch(API_URL + '/categories', { headers: { 'Content-Type': 'application/json' } })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setCategories(data.categories);
          sessionStorage.setItem(key, JSON.stringify(data.categories));
        }
      });
  };

  const getProperties = (page = 1, append = false) => {
    append ? setLoadingMore(true) : setPropertiesLoading(true);
    const qs = new URLSearchParams({ ...formValues, page }).toString();
    fetch(API_URL + '/offices/' + params.id + '/search?' + qs, {
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
    })
    .then(r => r.json())
    .then(data => {
      if (data.success) {
        const newItems = data.properties?.data || [];
        const updated  = append ? [...properties, ...newItems] : newItems;
        const total    = data.properties?.total || 0;
        const more     = data.properties?.current_page < data.properties?.last_page;
        const pg       = data.properties?.current_page || 1;

        setProperties(updated);
        setTotalProperties(total);
        setHasMorePages(more);
        setCurrentPage(pg);

        const cacheKey = `office_data_${params.id}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          sessionStorage.setItem(cacheKey, JSON.stringify({
            ...parsed, properties: updated,
            totalProperties: total, hasMorePages: more, currentPage: pg
          }));
        }
      }
      setPropertiesLoading(false);
      setLoadingMore(false);
    })
    .catch(() => { setPropertiesLoading(false); setLoadingMore(false); });
  };

  const loadMore = () => { if (!loadingMore && hasMorePages) getProperties(currentPage + 1, true); };

  const search = () => { setCurrentPage(1); setHasMorePages(false); setFilterOpen(false); getProperties(1, false); };

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: office?.name, text: 'قم بزيارة المكتب على موقع عقاراتي', url: 'https://aqaraty.ly/ar/office/' + office?.id }).catch(() => {});
    } else {
      alert('المشاركة غير متاحة على هذا المتصفح');
    }
  };

  const officeName = office?.office?.name ?? office?.name ?? '';
  const phoneNum   = getOfficePhoneNumberWithoutCountryCode(office);

  return (
    <MainLayout isTopBar screenTitle={t('office.title')}>
      {officeLoading ? (
        <div className="loader-container mt-3"><div className="loader"></div></div>
      ) : (
        <Fragment>

          {/* ── HERO ── */}
          <div className="office-hero">
            <div className="office-hero__bg" />

            <div className="office-hero__content">
              <div className="office-hero__avatar-ring">
                <img
                  src={office?.profile_picture_path
                    ? STORAGE_URL + office.profile_picture_path
                    : require('../../assets/images/default-office.png')}
                  alt={officeName}
                  className="office-hero__avatar"
                />
              </div>

              <h2 className="office-hero__name">
                {officeName}
                {office?.is_verified && <RiVerifiedBadgeFill size={20} className="office-hero__verified" />}
              </h2>
              <p className="office-hero__subtitle">مكتب عقاري</p>

              <div className="office-hero__stats">
                <div className="office-hero__stat">
                  <span className="office-hero__stat-val">{totalProperties}</span>
                  <span className="office-hero__stat-lbl">إعلان</span>
                </div>
              </div>

              {/* Social action buttons */}
              <div className="office-hero__actions">
                <a href={'tel:' + getOfficePhoneNumber(office)} className="office-action-btn office-action-btn--primary">
                  <FaPhoneAlt size={17} />
                  <span dir="ltr">{phoneNum}</span>
                </a>

                {office?.phone_number && (
                  <a href={'https://wa.me/' + getOfficeWhatsAppNumber(office)} target="_blank" rel="noreferrer" className="office-action-btn office-action-btn--whatsapp">
                    <FaWhatsapp size={19} />
                    <span>واتساب</span>
                  </a>
                )}

                {office?.instagram_link?.trim() !== '' && (
                  <a href={handleInstagramLink(office.instagram_link)} target="_blank" rel="noreferrer" className="office-action-btn">
                    <FaInstagram size={19} />
                  </a>
                )}

                {office?.tiktok_link?.trim() !== '' && (
                  <a href={handleTikTokLink(office.tiktok_link)} target="_blank" rel="noreferrer" className="office-action-btn">
                    <FaTiktok size={17} />
                  </a>
                )}

                <button type="button" className="office-action-btn" onClick={share}>
                  <IoShareOutline size={19} />
                </button>
              </div>
            </div>
          </div>

          {/* ── PROPERTIES SECTION ── */}
          <div className="office-properties">
            <Container>

              {/* Header bar */}
              <div className="office-props-header">
                <h3 className="office-props-title">
                  {t('office.ads.title')}
                  <span className="office-props-count">{totalProperties}</span>
                </h3>
                <button
                  className={'office-filter-toggle' + (filterOpen ? ' active' : '')}
                  onClick={() => setFilterOpen(v => !v)}
                >
                  <IoSearchOutline size={17} />
                  {t('office.ads.search.button')}
                </button>
              </div>

              {/* Collapsible filter bar */}
              {filterOpen && (
                <div className="office-filter-bar">
                  <Row className="g-2 align-items-end">
                    <Col xs="6" md="3">
                      <select
                        className="form-select"
                        value={formValues.property_for}
                        onChange={e => setFormValues({ ...formValues, property_for: e.target.value })}
                      >
                        <option value="">{t('office.ads.search.fields.purpose.label')}</option>
                        <option value="rent">للإيجار</option>
                        <option value="sale">للبيع</option>
                        <option value="exchange">للمقايضة</option>
                      </select>
                    </Col>

                    <Col xs="6" md="3">
                      <select
                        className="form-select"
                        value={formValues.category_id}
                        onChange={e => setFormValues({ ...formValues, category_id: e.target.value })}
                      >
                        <option value="">{t('office.ads.search.fields.category.label')}</option>
                        {categories.map((c, i) => (
                          <option key={i} value={c.id}>{tField(c, 'name')}</option>
                        ))}
                      </select>
                    </Col>

                    <Col xs="12" md="4">
                      <Select
                        placeholder={t('office.ads.search.fields.city.label')}
                        options={states?.map(s => ({
                          label: tField(s, 'name'),
                          options: s.cities.map(c => ({ value: c.id, label: tField(c, 'name') }))
                        }))}
                        onChange={opts => setFormValues({ ...formValues, city_ids: opts.map(o => o.value) })}
                        styles={SelectCustomStyles}
                        isMulti
                        closeMenuOnSelect={false}
                      />
                    </Col>

                    <Col xs="12" md="2">
                      <DefaultButton onClick={search} block>
                        {t('office.ads.search.button')}
                      </DefaultButton>
                    </Col>
                  </Row>
                </div>
              )}

              {/* Properties grid */}
              {propertiesLoading ? (
                <div className="loader-container py-5"><div className="loader"></div></div>
              ) : (
                <>
                  <Row className="g-3">
                    {properties?.length > 0 ? (
                      properties.map((data, key) => (
                        <Col xl="3" lg="4" md="4" sm="6" xs="12" key={key}>
                          <PropertyCard property={data} />
                        </Col>
                      ))
                    ) : (
                      <Col xs="12" className="text-center py-5">
                        <EmptyIcon />
                        <h6 className="mt-4 fw-normal text-muted">لا توجد عقارات</h6>
                      </Col>
                    )}
                  </Row>

                  {loadingMore && (
                    <div className="text-center py-4">
                      <div className="loader mx-auto"></div>
                    </div>
                  )}
                </>
              )}

            </Container>
          </div>

        </Fragment>
      )}
    </MainLayout>
  );
}

export default OfficePage;
